import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Database, 
  Activity, 
  Users, 
  Trophy,
  Settings,
  ChevronRight
} from 'lucide-react';

const AdminNavigation = () => {
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/admin' && location.pathname === '/admin') {
      return true;
    }
    return location.pathname.startsWith(path) && path !== '/admin';
  };

  const navigationItems = [
    {
      path: '/admin',
      label: 'Demo Management',
      icon: FileText,
      description: 'View and manage demo files and skin extractions'
    },
    {
      path: '/admin/crawler',
      label: 'Crawler Management',
      icon: Activity,
      description: 'Process demos and manage crawler operations'
    },
    {
      path: '/admin/hltv',
      label: 'HLTV Data',
      icon: Database,
      description: 'Teams, players, and rankings from HLTV'
    },
    {
      path: '/admin/matches',
      label: 'Match Results',
      icon: Trophy,
      description: 'View match results and statistics'
    },
    {
      path: '/admin/skins',
      label: 'Skin Database',
      icon: Settings,
      description: 'Manage and view all CS2 skins from ByMykel API'
    }
  ];

  return (
    <nav className="bg-gray-800 border-b border-gray-700 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Navigation Items */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    relative px-4 py-2 rounded-lg transition-all duration-200 group
                    ${active 
                      ? 'bg-primary-600 text-white shadow-lg' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }
                  `}
                >
                  <div className="flex items-center space-x-2">
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  
                  {/* Tooltip */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-gray-900 text-white text-sm rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl border border-gray-600">
                    <div className="text-center">
                      <div className="font-medium text-primary-300 mb-1">{item.label}</div>
                      <div className="text-gray-400">{item.description}</div>
                    </div>
                    {/* Arrow */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <select 
              value={location.pathname} 
              onChange={(e) => window.location.href = e.target.value}
              className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {navigationItems.map((item) => (
                <option key={item.path} value={item.path}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-gray-700 border-t border-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-2 text-sm">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors duration-200">
              Home
            </Link>
            <ChevronRight className="h-4 w-4 text-gray-500 mx-2" />
            <Link to="/admin" className="text-gray-400 hover:text-white transition-colors duration-200">
              Admin
            </Link>
            <ChevronRight className="h-4 w-4 text-gray-500 mx-2" />
            <span className="text-white font-medium">
              {navigationItems.find(item => isActive(item.path))?.label || 'Demo Management'}
            </span>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AdminNavigation;
